import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ClipboardDocumentListIcon, PlayIcon, TrashIcon } from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import {
  AppPage, BTN_DANGER, BTN_PRIMARY, BTN_SECONDARY, CARD, Empty, PageHeader, Pill, Segmented,
} from '../components/UI/AppUI';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` });

const FILTERS = [
  { value: 'all', label: 'All', status: 'in_progress,paused,completed,submitted' },
  { value: 'saved', label: 'In progress', status: 'in_progress,paused' },
  { value: 'history', label: 'Completed', status: 'completed,submitted' },
];
const PASS_MARK = 75;

const scopeOf = (s) => {
  const parts = [];
  const ch = s.selected_chapters?.length || 0;
  parts.push(ch === 1 ? `Chapter ${s.selected_chapters[0]}` : ch > 1 ? `${ch} chapters` : 'All chapters');
  const tp = s.selected_topics?.length || 0;
  if (tp) parts.push(tp === 1 ? `Topic ${s.selected_topics[0]}` : `${tp} topics`);
  return parts.join(' · ');
};

const ScoreBadge = ({ value }) => {
  const passed = value >= PASS_MARK;
  return (
    <span className={`flex h-14 w-14 flex-shrink-0 flex-col items-center justify-center rounded-2xl ${passed ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300' : 'bg-rose-500/10 text-rose-700 dark:text-rose-300'}`}>
      <span className="text-base font-semibold tabular-nums leading-none">{Math.round(value)}%</span>
      <span className="mt-1 text-[10px] font-medium uppercase tracking-wider opacity-80">{passed ? 'Pass' : 'Below'}</span>
    </span>
  );
};

const Tests2 = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [confirm, setConfirm] = useState(null); // array of ids to delete
  const [deleting, setDeleting] = useState(false);
  const [all, setAll] = useState([]); // unfiltered, for the summary numbers

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const status = FILTERS.find((f) => f.value === filter).status;
      const r = await fetch(`${API_URL}/api/exams/sessions/?status=${status}`, { headers: authHeaders() });
      if (r.ok) {
        const d = await r.json();
        const list = (d.results || d || []).sort((a, b) => new Date(b.started_at) - new Date(a.started_at));
        setSessions(list);
        if (filter === 'all') setAll(list);
      }
    } catch {
      toast.error('Could not load your tests');
    } finally {
      setLoading(false);
      setSelected(new Set());
    }
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const remove = async (ids) => {
    setDeleting(true);
    try {
      const results = await Promise.all(ids.map((id) => fetch(`${API_URL}/api/exams/sessions/${id}/`, { method: 'DELETE', headers: authHeaders() })));
      const failed = results.filter((r) => !r.ok).length;
      if (failed) toast.error(`${failed} test(s) could not be deleted`);
      else toast.success(ids.length > 1 ? `Deleted ${ids.length} tests` : 'Test deleted');
      window.dispatchEvent(new Event('examSaved'));
      if (filter !== 'all') setAll((cur) => cur.filter((s) => !ids.includes(s.id)));
      load();
    } finally {
      setDeleting(false);
      setConfirm(null);
    }
  };

  const toggle = (id) => setSelected((cur) => {
    const next = new Set(cur);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const done = all.filter((s) => s.status === 'completed' || s.status === 'submitted');
  const scored = done.filter((s) => s.score_percentage != null);
  const avg = scored.length ? Math.round(scored.reduce((n, s) => n + Number(s.score_percentage), 0) / scored.length) : null;
  const best = scored.length ? Math.round(Math.max(...scored.map((s) => Number(s.score_percentage)))) : null;
  const inProgress = all.length - done.length;

  return (
    <AppPage wide>
      <PageHeader eyebrow="Practice" title="My tests" action={<Link to="/dashboard" className={BTN_PRIMARY}><PlayIcon className="h-4 w-4" />Start a new test</Link>}>
        Resume saved tests and review the ones you&apos;ve finished.
      </PageHeader>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[
          { label: 'In progress', value: inProgress },
          { label: 'Completed', value: done.length },
          { label: 'Average score', value: avg !== null ? `${avg}%` : '–' },
          { label: 'Best score', value: best !== null ? `${best}%` : '–' },
        ].map((s) => (
          <div key={s.label} className={`p-5 ${CARD}`}>
            <p className="text-sm text-slate-500 dark:text-gray-400">{s.label}</p>
            <p className="mt-2 text-3xl font-semibold tracking-tight text-slate-900 tabular-nums dark:text-white">{s.value}</p>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap items-center justify-between gap-3">
        <Segmented label="Filter tests" size="md" value={filter} onChange={setFilter} options={FILTERS} />
        {sessions.length > 0 && (
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-slate-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={selected.size === sessions.length}
                onChange={(e) => setSelected(e.target.checked ? new Set(sessions.map((s) => s.id)) : new Set())}
                className="h-4 w-4 rounded border-slate-300 text-primary-600 focus:ring-primary-500 dark:border-white/20 dark:bg-white/5"
              />
              Select all
            </label>
            {selected.size > 0 && (
              <button onClick={() => setConfirm([...selected])} className={`${BTN_DANGER} px-3 py-1.5`}>
                <TrashIcon className="h-4 w-4" />Delete {selected.size}
              </button>
            )}
          </div>
        )}
      </div>

      <section className={`mt-4 ${CARD}`}>
        {loading ? (
          <div className="flex h-40 items-center justify-center"><div className="h-7 w-7 animate-spin rounded-full border-b-2 border-primary-600" /></div>
        ) : sessions.length === 0 ? (
          <Empty
            icon={ClipboardDocumentListIcon}
            title={filter === 'saved' ? 'Nothing in progress' : filter === 'history' ? 'No finished tests yet' : 'No tests yet'}
            action={<Link to="/dashboard" className={BTN_PRIMARY}>Pick a subject</Link>}
          >
            Start a practice test or a real exam from any subject and it will show up here.
          </Empty>
        ) : (
          <ul className="divide-y divide-slate-200 dark:divide-white/10">
            {sessions.map((s) => {
              const finished = s.status === 'completed' || s.status === 'submitted';
              const score = s.score_percentage != null ? Number(s.score_percentage) : null;
              return (
                <li key={s.id} className={`flex flex-wrap items-center gap-4 px-4 py-4 sm:flex-nowrap sm:px-5 ${selected.has(s.id) ? 'bg-primary-50/60 dark:bg-primary-600/10' : ''}`}>
                  <input
                    type="checkbox"
                    checked={selected.has(s.id)}
                    onChange={() => toggle(s.id)}
                    aria-label={`Select ${s.title || s.subject?.name}`}
                    className="h-4 w-4 flex-shrink-0 rounded border-slate-300 text-primary-600 focus:ring-primary-500 dark:border-white/20 dark:bg-white/5"
                  />
                  {finished && score !== null
                    ? <ScoreBadge value={score} />
                    : (
                      <span className="flex h-14 w-14 flex-shrink-0 items-center justify-center rounded-2xl bg-primary-600/10 text-xs font-bold tracking-wide text-primary-700 dark:text-primary-300">
                        {s.subject?.code || '—'}
                      </span>
                    )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <p className="truncate font-medium text-slate-900 dark:text-white">{s.subject?.name || s.title}</p>
                      <Pill tone={s.mode === 'practice' ? 'blue' : 'amber'}>{s.mode === 'practice' ? 'Practice' : 'Real exam'}</Pill>
                      {!finished && <Pill tone="gray">In progress</Pill>}
                    </div>
                    <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">
                      {scopeOf(s)} · {s.total_questions} questions
                      {finished && score !== null ? ` · ${s.questions_correct || 0} correct` : ''}
                      {' · '}{new Date(s.started_at).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                  <div className="flex w-full items-center justify-end gap-2 sm:w-auto">
                    {finished ? (
                      <>
                        <Link to={`/exam/${s.id}/results`} className={`${BTN_SECONDARY} px-3 py-1.5`}>Results</Link>
                        <Link to={`/exam/${s.id}/review`} className={`${BTN_SECONDARY} px-3 py-1.5`}>Review</Link>
                      </>
                    ) : (
                      <button onClick={() => navigate(`/exam/${s.id}`)} className={`${BTN_PRIMARY} px-3 py-1.5`}><PlayIcon className="h-4 w-4" />Resume</button>
                    )}
                    <button onClick={() => setConfirm([s.id])} className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-rose-500/10 hover:text-rose-600" aria-label="Delete test" title="Delete">
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      {confirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm" onClick={() => !deleting && setConfirm(null)}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-white/10 dark:bg-slate-900">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-rose-500/10"><TrashIcon className="h-5 w-5 text-rose-600" /></span>
            <h2 className="mt-4 text-lg font-semibold text-slate-900 dark:text-white">Delete {confirm.length > 1 ? `${confirm.length} tests` : 'this test'}?</h2>
            <p className="mt-1 text-sm text-slate-500 dark:text-gray-400">Their answers and scores are removed for good. This can&apos;t be undone.</p>
            <div className="mt-6 flex justify-end gap-3">
              <button onClick={() => setConfirm(null)} disabled={deleting} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={() => remove(confirm)} disabled={deleting} className={BTN_DANGER}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </AppPage>
  );
};

export default Tests2;
